/**
 * launchFragment — reading the one-time launch code the website hands us at boot.
 *
 * PURE: no DOM, no `import.meta`, no logging decisions. The caller passes
 * `location.hash` in and decides what to do with the result, exactly as
 * `gate.ts` does for `location.search`, so every rule below is unit-testable
 * with no browser.
 *
 * WHY THE FRAGMENT. The website opens us with `#launch=<code>`. A fragment is
 * never sent in the HTTP request line, so the code stays out of the game's
 * access logs, out of any CDN in front of it, and out of the `Referer` header
 * when the player navigates on. A query string would put a login-grade
 * credential in all three.
 *
 * THE FRAGMENT IS SHARED. `#p=<base64>` already carries a movement-tuning
 * override (`tuningFromHash` in app/mount.ts), and that check is a literal
 * `startsWith('#p=')`. So this parses the fragment as `&`-separated key/value
 * pairs and `hashWithoutLaunch` rebuilds it minus the launch key — blanket
 * clearing the hash would break tuning-share URLs, and prepending to it would
 * break that `startsWith`.
 *
 * NOTHING HERE MAY LOG OR ECHO A CODE. A warning says a code was malformed; it
 * never says what it was.
 */

/** The launch key, as written by the website's play page. */
const LAUNCH_KEY = 'launch';

/**
 * `secrets.token_urlsafe` output: base64url, unpadded. Anything outside this
 * alphabet did not come from the mint, and a length floor keeps a stray
 * `#launch=` from being treated as a real credential.
 */
const CODE_PATTERN = /^[A-Za-z0-9_-]{16,512}$/;

export interface LaunchFragment {
  /** The code to exchange, or null when there is nothing usable to exchange. */
  code: string | null;
  /** Human-readable degradations. Empty on a clean URL. The caller logs these ONCE. */
  warnings: readonly string[];
}

/** Split a fragment into pairs, tolerating a leading '#' and empty segments. */
function pairs(hash: string): [string, string][] {
  const body = hash.startsWith('#') ? hash.slice(1) : hash;
  if (body === '') return [];
  return body
    .split('&')
    .filter((part) => part !== '')
    .map((part) => {
      const eq = part.indexOf('=');
      // Split on the FIRST '=' only: a base64 `p=` value may contain more.
      return eq === -1
        ? ([part, ''] as [string, string])
        : ([part.slice(0, eq), part.slice(eq + 1)] as [string, string]);
    });
}

/**
 * Read the launch code out of a fragment.
 *
 * Never throws (hard rule 6). A missing code is the NORMAL case — every dev
 * session and every world without a session opens with no fragment at all —
 * so its absence is not a warning. A code that is present but malformed IS
 * warned about, because that means the handoff was attempted and failed.
 */
export function parseLaunchFragment(hash: string): LaunchFragment {
  const warnings: string[] = [];
  const found = pairs(hash).find(([k]) => k === LAUNCH_KEY);
  if (!found) return { code: null, warnings };

  let raw = found[1];
  try {
    raw = decodeURIComponent(raw);
  } catch {
    // A lone '%' makes decodeURIComponent throw. Treat it as malformed rather
    // than letting it reach the network.
    return {
      code: null,
      warnings: ['launch code in the URL could not be decoded — relaunch from the website'],
    };
  }

  if (!CODE_PATTERN.test(raw)) {
    return {
      code: null,
      warnings: ['launch code in the URL is malformed — relaunch from the website'],
    };
  }

  return { code: raw, warnings };
}

/**
 * The same fragment with the launch key removed, ready for `history.replaceState`.
 *
 * Returns '' when nothing is left, so the caller can drop the '#' entirely
 * rather than leaving a bare one in the address bar. Anything else keeps its
 * original order, which is what lets a `#p=` tuning override survive being
 * opened with a launch code beside it.
 */
export function hashWithoutLaunch(hash: string): string {
  const kept = pairs(hash).filter(([k]) => k !== LAUNCH_KEY);
  if (kept.length === 0) return '';
  return `#${kept.map(([k, v]) => (v === '' ? k : `${k}=${v}`)).join('&')}`;
}
